import { Accordion, Button, Modal, Stack, Text, Title } from "@assetwatch/ui";
import { Trans, useTranslation } from "react-i18next";

type Props = {
  open: boolean;
  onClose: () => void;
};

const styles = {
  accordionList: "mt-(--spacing-4)",
  table:
    "mt-(--spacing-8) w-full border-collapse text-(length:--font-size-body-md) [&_td]:border [&_td]:border-outline-subtle [&_td]:px-(--spacing-10) [&_td]:py-(--spacing-8) [&_td]:text-left [&_td]:align-top [&_th]:border [&_th]:border-outline-subtle [&_th]:bg-surface-container-high/[0.35] [&_th]:px-(--spacing-10) [&_th]:py-(--spacing-8) [&_th]:text-left [&_th]:align-top [&_th]:font-semibold",
  mono: "font-mono text-(length:--font-size-body-md)",
} as const;

function HelpSection({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <Accordion defaultOpen={defaultOpen} title={title}>
      {children}
    </Accordion>
  );
}

export function MaturityScoreHelpOverlay({ open, onClose }: Props) {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
      title={t("enterprise.maturity.helpOverlay.title")}
      description={t("enterprise.maturity.helpOverlay.subtitle")}
      footer={
        <Button type="button" variant="outline" onClick={onClose}>
          {t("enterprise.maturity.helpOverlay.closeButton")}
        </Button>
      }
    >
      <div data-testid="maturity-help-overlay">
        <div className={styles.accordionList}>
          <Stack gap={12}>
            <HelpSection
              title={t(
                "enterprise.maturity.helpOverlay.section.plainLanguage.title",
              )}
              defaultOpen
            >
              <Text>
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.plainLanguage.paragraph1"
                  components={{ strong: <strong /> }}
                />
              </Text>
              <Text>
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.plainLanguage.paragraph2"
                  components={{ strong: <strong /> }}
                />
              </Text>
            </HelpSection>

            <HelpSection
              title={t(
                "enterprise.maturity.helpOverlay.section.categories.title",
              )}
            >
              <Text size="sm">
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.categories.intro"
                  components={{
                    strong: <strong />,
                    code: <span className={styles.mono} />,
                  }}
                />
              </Text>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>
                      {t(
                        "enterprise.maturity.helpOverlay.section.categories.colHeaderCategory",
                      )}
                    </th>
                    <th>
                      {t(
                        "enterprise.maturity.helpOverlay.section.categories.colHeaderWeight",
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.categories.rowAlertOutcomes",
                      )}
                    </td>
                    <td>4.5</td>
                  </tr>
                  <tr>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.categories.rowExecutionSpeed",
                      )}
                    </td>
                    <td>4.5</td>
                  </tr>
                  <tr>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.categories.rowValueRealization",
                      )}
                    </td>
                    <td>5.0</td>
                  </tr>
                  <tr>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.categories.rowAppEngagement",
                      )}
                    </td>
                    <td>2.5</td>
                  </tr>
                  <tr>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.categories.rowEmailEngagement",
                      )}
                    </td>
                    <td>1.0</td>
                  </tr>
                </tbody>
              </table>
            </HelpSection>

            <HelpSection
              title={t(
                "enterprise.maturity.helpOverlay.section.dataSources.title",
              )}
            >
              <Text size="sm">
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.dataSources.intro"
                  components={{ code: <span className={styles.mono} /> }}
                />
              </Text>
              <Title size="sm" as="h3">
                {t(
                  "enterprise.maturity.helpOverlay.section.dataSources.facilityRankingTitle",
                )}
              </Title>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.colHeaderColumn",
                      )}
                    </th>
                    <th>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.colHeaderUseInApi",
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={styles.mono}>ScoreAlertOutcome</td>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.facilityRankingRow1Use",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className={styles.mono}>ScoreResponsiveness</td>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.facilityRankingRow2Use",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className={styles.mono}>ScoreAppEngagement</td>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.facilityRankingRow3Use",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className={styles.mono}>ScoreEmailEngagement</td>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.facilityRankingRow4Use",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className={styles.mono}>Explainability</td>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.facilityRankingRow5Use",
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
              <Text size="sm" tone="subtle">
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.dataSources.subtleProducedUpstream"
                  components={{ strong: <strong /> }}
                />
              </Text>

              <Title size="sm" as="h3">
                {t(
                  "enterprise.maturity.helpOverlay.section.dataSources.facilityCuratedTitle",
                )}
              </Title>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.colHeaderColumn",
                      )}
                    </th>
                    <th>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.colHeaderUse",
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className={styles.mono}>
                      AssetAlertsAverageTimeToRepair
                    </td>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.facilityCuratedRow1Use",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className={styles.mono}>
                      AssetAlertsAverageTimeToComment
                    </td>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.facilityCuratedRow2Use",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className={styles.mono}>AssetAlertsActualCostSaving</td>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.facilityCuratedRow3Use",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className={styles.mono}>MachineCount</td>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.facilityCuratedRow4Use",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className={styles.mono}>AssetAlertsResolved</td>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.facilityCuratedRow5Use",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className={styles.mono}>AssetAlertsWatching</td>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.facilityCuratedRow6Use",
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className={styles.mono}>
                      AssetAlertsMaintenanceRecommenmdation
                    </td>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.dataSources.facilityCuratedRow7Use",
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </HelpSection>

            <HelpSection
              title={t(
                "enterprise.maturity.helpOverlay.section.calculations.title",
              )}
            >
              <Text size="sm">
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.calculations.percentilePool"
                  components={{
                    strong: <strong />,
                    code: <span className={styles.mono} />,
                  }}
                />
              </Text>
              <Text size="sm">
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.calculations.executionSpeed"
                  components={{
                    strong: <strong />,
                    code: <span className={styles.mono} />,
                  }}
                />
              </Text>
              <Text size="sm">
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.calculations.valueRealization"
                  components={{
                    strong: <strong />,
                    code: <span className={styles.mono} />,
                  }}
                />
              </Text>
              <Text size="sm">
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.calculations.overall"
                  components={{
                    strong: <strong />,
                    code: <span className={styles.mono} />,
                  }}
                />
              </Text>
            </HelpSection>

            <HelpSection
              title={t("enterprise.maturity.helpOverlay.section.tier.title")}
            >
              <Text size="sm">
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.tier.intro"
                  components={{ code: <span className={styles.mono} /> }}
                />
              </Text>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>
                      {t(
                        "enterprise.maturity.helpOverlay.section.tier.colHeaderScore",
                      )}
                    </th>
                    <th>
                      {t(
                        "enterprise.maturity.helpOverlay.section.tier.colHeaderTier",
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.tier.scoreUnder50",
                      )}
                    </td>
                    <td>{t("enterprise.maturity.tiers.reactive")}</td>
                  </tr>
                  <tr>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.tier.score50to65",
                      )}
                    </td>
                    <td>{t("enterprise.maturity.tiers.emerging")}</td>
                  </tr>
                  <tr>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.tier.score65to75",
                      )}
                    </td>
                    <td>{t("enterprise.maturity.tiers.managed")}</td>
                  </tr>
                  <tr>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.tier.score75to85",
                      )}
                    </td>
                    <td>{t("enterprise.maturity.tiers.proactive")}</td>
                  </tr>
                  <tr>
                    <td>
                      {t(
                        "enterprise.maturity.helpOverlay.section.tier.score85AndAbove",
                      )}
                    </td>
                    <td>{t("enterprise.maturity.tiers.optimized")}</td>
                  </tr>
                </tbody>
              </table>
            </HelpSection>

            <HelpSection
              title={t(
                "enterprise.maturity.helpOverlay.section.topPctGlobally.title",
              )}
            >
              <Text size="sm">
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.topPctGlobally.paragraph"
                  components={{
                    strong: <strong />,
                    code: <span className={styles.mono} />,
                  }}
                />
              </Text>
            </HelpSection>

            <HelpSection
              title={t(
                "enterprise.maturity.helpOverlay.section.insightsText.title",
              )}
            >
              <Text size="sm">
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.insightsText.paragraph"
                  components={{
                    strong: <strong />,
                    code: <span className={styles.mono} />,
                  }}
                />
              </Text>
            </HelpSection>

            <HelpSection
              title={t(
                "enterprise.maturity.helpOverlay.section.codePaths.title",
              )}
            >
              <Text size="sm">
                <Trans
                  i18nKey="enterprise.maturity.helpOverlay.section.codePaths.paragraph"
                  components={{
                    code: <span className={styles.mono} />,
                    br: <br />,
                  }}
                />
              </Text>
            </HelpSection>
          </Stack>
        </div>
      </div>
    </Modal>
  );
}
